import { useContext } from "react";
import { convertFromServerTime, formatDateString } from "../../../utils";
import { DateTimeRange, DateTimeRangeField, formatDateTimeRangeValueToObject } from "../../form";
import { FilterExpressionChildInput, FilterExpressionOperatorEnum, FilterOperatorEnum, GraphQLTableColumn } from "../../GraphQL";
import { AppConfig, AppConfigContext } from "../AppConfigContext";

export function DateTimeColumnTemplate<T extends object = {}>(params?: GraphQLTableColumn<T> & {
  allowEmptyDateOnFilter?: boolean
  formatAsTime?: boolean
  formatAsDate?: boolean
  serverTimeZone?: string,
}): GraphQLTableColumn<T> {
  return {
    Header: "",
    Cell: ({ value, bodyCellProps }: any) => {
      const appConfig = useContext(AppConfigContext);

      let format = appConfig?.dataTimeFormatMoment;
      if (params?.formatAsDate) {
        format = appConfig?.dataFormatMoment;
      } else if (params?.formatAsTime) {
        format = appConfig?.timeFormatMoment;
      }

      const prettified = formatDateString(value, format, appConfig?.timeZone, params?.serverTimeZone || appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment);
      return <span title={prettified} {...bodyCellProps}>{prettified}</span>;
    },
    minWidth: 135,
    width: 135,
    filterExpressionBuilder: (p): FilterExpressionChildInput => {
      if (p.value === null) {
        return ({
          entry: {
            path: p.path,
            operator: FilterOperatorEnum.Null,
          },
        });
      }
      return {
        expr: {
          operator: FilterExpressionOperatorEnum.And,
          children: [
            ...(p.value.from ? [ {
              entry: {
                path: p.path,
                operator: FilterOperatorEnum.GreaterEqual,
                value: p.value.from,
              },
            } ] : []),
            ...(p.value.to ? [ {
              entry: {
                path: p.path,
                operator: FilterOperatorEnum.LowerEqual,
                value: p.value.to,
              },
            } ] : []),
          ],
        },
      };
    },
    filterValueModifier: (value: DateTimeRange, context: AppConfig) =>
      formatDateTimeRangeValueToObject(
        value,
        context.serverDateTimeFormatMoment,
        context.timeZone,
        context.serverTimeZone,
      ),
    Filter: ({ column }) =>
      <DateTimeRangeField
        placeholder="Find..."
        value={column.filterValue}
        onChange={v => column.setFilter(v)}
        allowEmpty={params?.allowEmptyDateOnFilter}
      />,
    ...params,
    excelProps: {
      valueModifier: (value, context) => value
        ? // hotfix for displaying dates in user selected time zone (Excel works with UTC by default)
        new Date(convertFromServerTime(value, context.serverTimeZone, context.serverDateTimeFormatMoment)
          .tz(context.timeZone)
          .format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z")
        : value,
      numFmt: params?.formatAsDate ? "mm/dd/yyyy" : (params?.formatAsTime ? "HH:MM" : "mm/dd/yyyy HH:MM"),
      width: 16,
      filterButton: true,
      ...params?.excelProps,
    },
  };
}

export default DateTimeColumnTemplate;
